<template>
    <two-column-layout>
        <card-container>
            <h2>{{ $t(i18n.company_profile_step.title) }}</h2>
            <p>{{ $t(i18n.company_profile_step.description) }}</p>

            <radio-group-fragment
              key="tradestockPresent"
              :label="$t(i18n.input.tradestock_present.label)"
              :description="$t(i18n.input.tradestock_present.description)"
              group-name="group company profile tradestock present"
              :options="radioGroupYesNoOptions"
              show-info
              v-model="form.tradestockPresent"
              :is-invalid="isFieldInvalid($v.form.tradestockPresent)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_TRADESTOCK_PRESENT')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.tradestockPresent" />
                </template>
            </radio-group-fragment>
            <fragment-separator></fragment-separator>

            <radio-group-fragment
              key="buildingOwnership"
              :label="$t(i18n.input.building_ownership.label)"
              :description="$t(i18n.input.building_ownership.description)"
              group-name="group company profile building ownership"
              :options="radioGroupYesNoOptions"
              show-info
              v-model="form.buildingOwnership"
              :is-invalid="isFieldInvalid($v.form.buildingOwnership)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_BUILDING_OWNERSHIP')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.buildingOwnership" />
                </template>
            </radio-group-fragment>
            <fragment-separator></fragment-separator>

            <radio-group-fragment v-if="isFieldAnswerEqual(form.buildingOwnership, 'n')"
              key="buildingRental"
              :label="$t(i18n.input.building_rental.label)"
              :description="$t(i18n.input.building_rental.description)"
              group-name="`group company profile building rental"
              :options="radioGroupYesNoOptions"
              show-info
              v-model="form.buildingRental"
              :is-invalid="isFieldInvalid($v.form.buildingRental)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_BUILDING_RENTAL')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.buildingRental" />
                </template>
            </radio-group-fragment>

            <info-block v-if="this.dropoutBuildingOwnershipOrRental" variant="warning" icon="phone">
                {{ $t(i18n.request_drop_out) }}
            </info-block>

            <div v-if="!this.dropoutBuildingOwnershipOrRental">
                <currency-input-fragment v-if="isFieldAnswerEqual(form.buildingOwnership, 'n') && isFieldAnswerEqual(form.buildingRental, 'y')"
                                         :label="$t(i18n.input.value_tenant_interest.label)"
                                         :description="$t(i18n.input.value_tenant_interest.description)"
                                         size="small"
                                         show-info
                                         v-model="form.valueTenantInterest"
                                         :precision="0"
                                         :is-invalid="isFieldInvalid($v.form.valueTenantInterest)"
                                         @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_VALUE_TENANT_INTEREST')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.valueTenantInterest" />
                    </template>
                </currency-input-fragment>
                <fragment-separator v-if="isFieldAnswerEqual(form.buildingOwnership, 'n')"></fragment-separator>

                <radio-group-fragment
                  key="inventoryPresent"
                  :label="$t(i18n.input.inventory_present.label)"
                  :description="$t(i18n.input.inventory_present.description)"
                  group-name="group company profile inventory present"
                  :options="radioGroupYesNoOptions"
                  show-info
                  v-model="form.inventoryPresent"
                  :is-invalid="isFieldInvalid($v.form.inventoryPresent)"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_INVENTORY_PRESENT')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.inventoryPresent" />
                    </template>
                </radio-group-fragment>
                <fragment-separator></fragment-separator>

                <radio-group-fragment
                  key="employees"
                  :label="$t(i18n.input.employees.label)"
                  :description="$t(i18n.input.employees.description)"
                  group-name="group company profile employees"
                  :options="radioGroupYesNoOptions"
                  show-info
                  v-model="form.employees"
                  :is-invalid="isFieldInvalid($v.form.employees)"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_EMPLOYEES')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.employees" />
                    </template>
                </radio-group-fragment>
                <fragment-separator></fragment-separator>

                <radio-group-fragment
                  key="thirdPartyVehicleStorage"
                  :label="$t(i18n.input.third_party_vehicle_storage.label)"
                  :description="$t(i18n.input.third_party_vehicle_storage.description)"
                  group-name="group company profile third party vehicle storage"
                  :options="radioGroupYesNoOptions"
                  show-info
                  v-model="form.thirdPartyVehicleStorage"
                  :is-invalid="isFieldInvalid($v.form.thirdPartyVehicleStorage)"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_THIRD_PARTY_VEHICLE_STORAGE')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.thirdPartyVehicleStorage" />
                    </template>
                </radio-group-fragment>
                <fragment-separator></fragment-separator>

                <radio-group-fragment
                  key="alarmWithReporting"
                  :label="$t(i18n.input.alarm_with_reporting.label)"
                  :description="$t(i18n.input.alarm_with_reporting.description)"
                  group-name="group company profile alarm with reporting"
                  :options="radioGroupYesNoOptions"
                  show-info
                  v-model="form.alarmWithReporting"
                  :is-invalid="isFieldInvalid($v.form.alarmWithReporting)"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_ALARM_WITH_REPORTING')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.alarmWithReporting" />
                    </template>
                </radio-group-fragment>
                <fragment-separator></fragment-separator>

                <radio-group-fragment
                  key="bovagMembership"
                  :label="$t(i18n.input.bovag_membership.label)"
                  :description="$t(i18n.input.bovag_membership.description)"
                  group-name="group company profile bovag membership"
                  :options="radioGroupYesNoOptions"
                  v-model="form.bovagMembership"
                  :is-invalid="isFieldInvalid($v.form.bovagMembership)"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.bovagMembership" />
                    </template>
                </radio-group-fragment>
            </div>

        </card-container>
        <button-group direction="right" top-spacing="l">
            <button-fragment icon-before="back"
                             as-link
                             @click="previousStep()">{{$t(i18n.button.previous)}}
            </button-fragment>
            <button-fragment @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
        </button-group>
    </two-column-layout>
</template>

<script>
    import {Utils} from "@bvm/core";
    import {
        CurrencyInputFragment,
        TwoColumnLayout,
        FragmentSeparator,
        RadioGroupFragment,
        ButtonFragment,
        ButtonGroup,
        CardContainer,
        InfoBlock
    } from "@bvm/forms";
    import {
        minValue,
        maxValue,
        required,
        decimal,
        requiredIf
    } from 'vuelidate/lib/validators'
    import FieldErrorMessages from "../../shared/field-error-messages";
    import MediaItemModal from "../../shared/media-item-modal";
    import {createNamespacedHelpers} from "vuex";

    const { mapState, mapMutations } = createNamespacedHelpers(
      "gpsQuoteRequest"
    );

    const i18nBuilder = new Utils.I18n.I18nBuilder("gps_quote_request_form")
      .add("company_profile_step", b => b.add("title").add("description"))
      .add("button", b => b.add("previous").add("next"))
      .add("radio_group", b => b.add("generic", c => c.add("yes").add("no")))
      .add("input", b => b
        .add("value_tenant_interest", c => c.add("label").add("description"))
        .add("tradestock_present", c => c.add("label").add("description"))
        .add("building_ownership", c => c.add("label").add("description"))
        .add("building_rental", c => c.add("label").add("description"))
        .add("inventory_present", c => c.add("label").add("description"))
        .add("employees", c => c.add("label").add("description"))
        .add("third_party_vehicle_storage", c => c.add("label").add("description"))
        .add("alarm_with_reporting", c => c.add("label").add("description"))
        .add("bovag_membership", c => c.add("label").add("description")));

    const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
      .add("minValue")
      .add("maxValue")
      .add("required")
      .add("decimal")
      .add("required");

    const i18nReusableBuilder = new Utils.I18n.I18nBuilder("reusable_label")
      .add("request_drop_out");

    export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys(), ...i18nReusableBuilder.getKeys()];

    export default {
        name: "company-profile-step",
        components: {
            FieldErrorMessages,
            CurrencyInputFragment,
            TwoColumnLayout,
            FragmentSeparator,
            RadioGroupFragment,
            ButtonFragment,
            ButtonGroup,
            CardContainer,
            InfoBlock
        },
        props: {},
        data() {
            return {
                i18n: {...i18nBuilder.getData(), ...i18nReusableBuilder.getData()},
                form : {
                    tradestockPresent : null,
                    buildingOwnership : null,
                    buildingRental : null,
                    valueTenantInterest : null,
                    inventoryPresent : null,
                    employees : null,
                    thirdPartyVehicleStorage : null,
                    alarmWithReporting : null,
                    bovagMembership : null
                }
            };
        },
        computed: {
            ...mapState(["companyProfile", "companyInfo"]),
            radioGroupYesNoOptions() {
                return {
                    y: this.$t(this.i18n.radio_group.generic.yes),
                    n: this.$t(this.i18n.radio_group.generic.no)
                };
            },
            dropoutBuildingOwnershipOrRental() {
                return this.isFieldAnswerEqual(this.form.buildingOwnership, "n")
                        && this.isFieldAnswerEqual(this.form.buildingRental, "n");
            }
        },
        methods: {
            ...mapMutations([
                "setCompanyProfile",
                "setUniformSalary",
                "setTradestockMaxValue",
                "setCompanyBuildingRebuildValue",
                "setValueInventory",
                "setValueGoods",
                "setValueTires"
            ]),
            isFieldInvalid(field) {
              return field.$dirty && field.$invalid;
            },
            isFieldAnswerEqual(fieldValue, answerToCompare) {
              return fieldValue === answerToCompare;
            },
            onInfoClicked(mediaItemTag) {
                this.$bvmModal.show(MediaItemModal, {
                    itemTag: mediaItemTag
                }, {
                    clickToClose: true
                });
            },
            previousStep(value) {
                this.$emit("previousStep", value);
            },
            resetOtherIfAnswerNotEqual(fieldValue, notEqualAnswer, fieldSetterCallback) {
              if (!this.isFieldAnswerEqual(fieldValue, notEqualAnswer)) {
                fieldSetterCallback(null);
              }
            },
            updateState() {
                // Reset all fields that are dependent on specific fields to be answered with 'yes', including state
                // of future steps.
                // We do this to purge potentially pre-existing data that becomes irrelevant when their
                // precondition fields in this step are answered with 'no', thus preventing irrelevant or incorrect
                // data from being saved/sent to the backend
                this.resetOtherIfAnswerNotEqual(this.form.buildingRental, 'y', this.setUniformSalary);
                this.resetOtherIfAnswerNotEqual(this.form.tradestockPresent, 'y', this.setTradestockMaxValue);
                this.resetOtherIfAnswerNotEqual(this.form.buildingOwnership, 'y', this.setCompanyBuildingRebuildValue);
                this.resetOtherIfAnswerNotEqual(this.form.inventoryPresent, 'y', this.setValueInventory);
                this.resetOtherIfAnswerNotEqual(this.form.inventoryPresent, 'y', this.setValueGoods);
                this.resetOtherIfAnswerNotEqual(this.form.inventoryPresent, 'y', this.setValueTires);

                if (this.isFieldAnswerEqual(this.form.buildingOwnership, 'y')) {
                  this.form.buildingRental = null;
                  this.form.valueTenantInterest = null;
                }

                if (this.isFieldAnswerEqual(this.form.buildingRental, 'n')) {
                  this.form.valueTenantInterest = null;
                }

                this.setCompanyProfile(this.form);
            },
            nextStep() {
                this.$v.$touch();
                if (this.dropoutBuildingOwnershipOrRental || this.$v.$invalid) {
                    this.$emit("onError");
                } else {
                    this.updateState();
                    this.$emit("nextStep");
                }
            }
        },
        created() {
            this.form = this.companyProfile;
        },
        validations() {
            return {
                form : {
                    tradestockPresent : { required },
                    buildingOwnership : { required },
                    buildingRental : {
                      required: requiredIf(() => this.form.buildingOwnership === 'n'),
                    },
                    valueTenantInterest : {
                        required: requiredIf(() => this.form.buildingRental === 'y'),
                        minValue: minValue(0),
                        maxValue: maxValue(1000000), //1_000_000
                        decimal
                    },
                    inventoryPresent : { required },
                    employees : { required },
                    thirdPartyVehicleStorage : { required },
                    alarmWithReporting : { required },
                    bovagMembership : { required }
                }
            }
        }
    }
</script>

<style>

</style>